import { Box, Skeleton, SkeletonGroup } from "@salestrak/ui";

import {
  SEAM_HEIGHT,
  STRIP_HEIGHT,
  STRIP_INSET,
} from "#app/components/forms/form-builder/form-canvas";
import type { CanvasDensity } from "#app/components/forms/form-builder/form-canvas-zoom";
import { FormFillCard, FormFillStack } from "#app/components/forms/form-fill/form-fill-body";

const MAX_CARDS = 4;

function skeletonCardCount(count: number): number {
  return Math.max(1, Math.min(count, MAX_CARDS));
}

function QuestionBars({ index }: { index: number }) {
  return (
    <Box direction="column" gap="base">
      <Box align="center" direction="row" minHeight={20}>
        <Skeleton height={12} vary={index} width={144} />
      </Box>
      <Skeleton className="rounded-md" fillWidth height={36} />
    </Box>
  );
}

/** Mirrors the real cards' seams so the column lands without a shift. */
export function FormCanvasSkeleton({
  count,
  density,
}: {
  count: number;
  density: CanvasDensity;
}) {
  return (
    <SkeletonGroup aria-hidden data-slot="form-canvas-skeleton">
      {Array.from({ length: skeletonCardCount(count) }, (_, index) => (
        <Box direction="column" key={index}>
          <Box height={SEAM_HEIGHT[density]} shrink={false} />
          <Box background="elevated" border direction="column" overflow="clip" rounded="lg">
            <Box
              align="center"
              border={density === "overview" ? undefined : "bottom"}
              direction="row"
              gap="sm"
              minHeight={STRIP_HEIGHT[density]}
              paddingLeft={STRIP_INSET[density].start}
              paddingRight={STRIP_INSET[density].end}
            >
              <Skeleton className="rounded-[3px]" height={12} width={12} />
              <Skeleton height={10} vary={index} width={density === "overview" ? 144 : 56} />
            </Box>
            {density === "full" ? (
              <Box direction="column" padding="lg">
                <QuestionBars index={index} />
              </Box>
            ) : density === "compact" ? (
              <Box direction="column" padding="md">
                <Skeleton height={12} vary={index} width={144} />
              </Box>
            ) : null}
          </Box>
        </Box>
      ))}
    </SkeletonGroup>
  );
}

export function FormFillCardsSkeleton({ count }: { count: number }) {
  return (
    <SkeletonGroup aria-hidden data-slot="form-fill-skeleton">
      <FormFillStack>
        {Array.from({ length: skeletonCardCount(count) }, (_, index) => (
          <FormFillCard key={index}>
            <QuestionBars index={index} />
          </FormFillCard>
        ))}
      </FormFillStack>
    </SkeletonGroup>
  );
}
